/**
 * Cliente API de residentes (Sprint 13 S13-32).
 *
 * Workflow: vecino agrega → comision valida (apruebe / rechaza con
 * motivo). Si el residente se va, se inactiva con `hasta`.
 */
import { apiFetch } from './client';

export type ParentescoLiteral =
  | 'titular'
  | 'conyuge'
  | 'hijo'
  | 'padre'
  | 'madre'
  | 'hermano'
  | 'inquilino'
  | 'personal_servicio'
  | 'otro';

export type EstadoResidente =
  | 'pendiente_validacion'
  | 'aprobado'
  | 'rechazado'
  | 'inactivo';

export interface ResidenteConPersonaRead {
  id: string;
  tenencia_id: string;
  persona_id: string;
  /** Si no es null, hay una designación de autorizado esperando a la Comisión
   *  para esta persona. Sin este dato el titular designa y no ve nada. */
  designacion_autorizado_pendiente_id?: string | null;
  parentesco: ParentescoLiteral;
  desde: string;
  hasta: string | null;
  estado: EstadoResidente;
  comprobante_identidad_url: string | null;
  validado_por: string | null;
  validado_at: string | null;
  motivo_rechazo: string | null;
  notas: string | null;
  /** S15-Vec.2: justificacion del vecino al editar un residente
   *  aprobado. Solo poblado mientras esta en pendiente_validacion. */
  motivo_pendiente?: string | null;
  /** Sprint 18 P4: flag del autorizado de portal (máx 1 por casa,
   *  además del titular). */
  cuenta_portal_activa: boolean;
  creado_at: string;
  actualizado_at: string;
  persona_nombre_completo: string;
  persona_rut: string | null;
  persona_email: string | null;
  /** Habilita la citofonía: es el número que se programa en la central. */
  persona_celular: string | null;
}

export interface ResidenteCreate {
  persona_id: string;
  parentesco: ParentescoLiteral;
  desde: string;
  comprobante_identidad_url?: string | null;
  notas?: string | null;
}

export interface ResidenteUpdate {
  parentesco?: ParentescoLiteral;
  desde?: string;
  comprobante_identidad_url?: string | null;
  notas?: string | null;
  /** S15-Vec.2: si el residente esta aprobado y el PATCH trae cambios
   *  efectivos, el backend exige justificacion no vacia y transiciona
   *  a pendiente_validacion. */
  justificacion_cambio?: string | null;
}

/**
 * Body de POST /me/residentes — crea persona + residente en una
 * sola transacción. Usado por el vecino desde el portal.
 */
export interface ResidenteCreateConPersona {
  nombre_completo: string;
  rut?: string | null;
  email?: string | null;
  celular?: string | null;
  lote_id: string;
  parentesco: ParentescoLiteral;
  desde: string;
  comprobante_identidad_url?: string | null;
  notas?: string | null;
  // Sprint 17 S17-17 — si true, el backend también crea una
  // SolicitudRecurso(tipo='llavero', motivo='nuevo_residente').
  necesita_llavero?: boolean;
}

export function agregarResidenteConPersonaNueva(
  data: ResidenteCreateConPersona,
): Promise<ResidenteConPersonaRead> {
  return apiFetch<ResidenteConPersonaRead>('/me/residentes', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function listarResidentesDeLote(
  loteId: string,
  incluirInactivos = false,
): Promise<ResidenteConPersonaRead[]> {
  const qs = incluirInactivos ? '?incluir_inactivos=true' : '';
  return apiFetch<ResidenteConPersonaRead[]>(
    `/lotes/${loteId}/residentes${qs}`,
  );
}

export function crearResidente(
  loteId: string,
  data: ResidenteCreate,
): Promise<ResidenteConPersonaRead> {
  return apiFetch<ResidenteConPersonaRead>(`/lotes/${loteId}/residentes`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function actualizarResidente(
  residenteId: string,
  data: ResidenteUpdate,
): Promise<ResidenteConPersonaRead> {
  return apiFetch<ResidenteConPersonaRead>(`/residentes/${residenteId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export function validarResidente(
  residenteId: string,
): Promise<ResidenteConPersonaRead> {
  return apiFetch<ResidenteConPersonaRead>(
    `/residentes/${residenteId}/validar`,
    { method: 'POST', body: JSON.stringify({}) },
  );
}

export function rechazarResidente(
  residenteId: string,
  motivoRechazo: string,
): Promise<ResidenteConPersonaRead> {
  return apiFetch<ResidenteConPersonaRead>(
    `/residentes/${residenteId}/rechazar`,
    {
      method: 'POST',
      body: JSON.stringify({ motivo_rechazo: motivoRechazo }),
    },
  );
}

export function inactivarResidente(
  residenteId: string,
  hasta: string,
): Promise<ResidenteConPersonaRead> {
  return apiFetch<ResidenteConPersonaRead>(
    `/residentes/${residenteId}/inactivar`,
    { method: 'POST', body: JSON.stringify({ hasta }) },
  );
}

/**
 * Sprint 17 Z22 — borra físicamente un residente en estado
 * pendiente_validacion (el vecino se arrepintió antes de la
 * aprobación). Cualquier solicitud de recurso activa asociada se
 * cancela en cascada. NO aplica para aprobado/inactivo/rechazado.
 */
export function eliminarResidente(residenteId: string): Promise<void> {
  return apiFetch<void>(`/residentes/${residenteId}`, { method: 'DELETE' });
}

/**
 * Body del PATCH /me/residentes/{id} — S15-Vec.3. Edita atómicamente
 * la Persona y el Residente del lote del vecino. Si está aprobado y
 * hay cambios efectivos, exige `justificacion_cambio` y transiciona
 * a pendiente_validacion.
 */
export interface ResidenteUpdateConPersona {
  nombre_completo?: string;
  rut?: string | null;
  email?: string | null;
  celular?: string | null;
  parentesco?: ParentescoLiteral;
  desde?: string;
  notas?: string | null;
  justificacion_cambio?: string | null;
}

export function actualizarResidenteConPersona(
  residenteId: string,
  data: ResidenteUpdateConPersona,
): Promise<ResidenteConPersonaRead> {
  return apiFetch<ResidenteConPersonaRead>(`/me/residentes/${residenteId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export interface DesignacionAutorizadoRead {
  id: string;
  residente_id: string;
  tenencia_id: string;
  justificacion: string;
  estado: string;
  solicitado_at: string;
  revisado_at: string | null;
  motivo_rechazo: string | null;
}

/**
 * El titular designa a un residente aprobado como autorizado de portal.
 *
 * Desde el 2026-09-03 **NO crea la cuenta**: deja una designación pendiente en
 * la cola de la Comisión, que es quien aprueba. La `justificacion` es
 * obligatoria — escribir el motivo obliga a darse cuenta de lo que se está
 * haciendo, y un clic por error no sobrevive a tener que explicarlo.
 *
 * El autorizado actual conserva su acceso hasta que se apruebe al nuevo.
 */
export function designarAutorizado(
  residenteId: string,
  justificacion: string,
): Promise<DesignacionAutorizadoRead> {
  return apiFetch<DesignacionAutorizadoRead>(
    `/me/residentes/${residenteId}/designar-autorizado`,
    { method: 'POST', body: JSON.stringify({ justificacion }) },
  );
}

/** El titular se arrepiente mientras la designación sigue pendiente. */
export function cancelarDesignacionAutorizado(
  designacionId: string,
): Promise<DesignacionAutorizadoRead> {
  return apiFetch<DesignacionAutorizadoRead>(
    `/me/designaciones-autorizado/${designacionId}/cancelar`,
    { method: 'POST' },
  );
}

/** Sprint 18 P4: el titular quita la designación de autorizado. */
export function desdesignarAutorizado(
  residenteId: string,
): Promise<ResidenteConPersonaRead> {
  return apiFetch<ResidenteConPersonaRead>(
    `/me/residentes/${residenteId}/desdesignar-autorizado`,
    { method: 'POST' },
  );
}
